/**
 * @file Shared-shell manual Compliance item, requirement, and record workflows.
 */

import {appData} from "../../app/state/state.js";
import {
    closeForm,
    setModalCloseCallback,
    setModalCloseGuard,
} from "../../shared/forms/modalShell.js";
import {openStandardForm} from "../../shared/forms/standardFormModal.js";
import {
    FIELD_LABEL_CLASSES,
    FIELD_WRAPPER_CLASSES,
    NATIVE_INPUT_CLASSES,
    REQUIRED_MARK_HTML,
    bindNativeFieldBehavior,
    validateNativeFormFields,
} from "../../shared/forms/nativeFieldStyles.js";
import {
    focusFirstRegisteredControlError,
    validateRegisteredControls,
} from "../../shared/ui/controls/controlCore.js";
import {createDateControl} from "../../shared/ui/controls/dateControl.js";
import {createSelectControl} from "../../shared/ui/controls/selectControl.js";
import {openConfirmationModal} from "../../shared/ui/confirmationModal.js";
import {escapeHTML} from "../../shared/utils/contentUtils.js";
import {
    correctComplianceRecord,
    createComplianceRecord,
    createComplianceRequirement,
    createComplianceTrackedItem,
    deleteComplianceRecord,
    saveComplianceTrackedItem,
} from "./complianceApi.js";
import {openComplianceEvidenceModal} from "./complianceEvidence.js";

let modalSession = 0;
let modalOpen = false;
let modalDirty = false;
let modalPending = false;
let dirtyFocusTarget = null;

function getBusinessToday() {
    const timeZone = appData.config?.businessCalendar?.timezone ||
        "Pacific/Auckland";
    try {
        return new Intl.DateTimeFormat("en-CA", {
            timeZone,
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).format(new Date());
    } catch {
        return new Intl.DateTimeFormat("en-CA", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
        }).format(new Date());
    }
}

function calculateNextDueDate(startDate, frequencyMonths) {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(startDate || "");
    const months = Number(frequencyMonths);
    if (!match || !Number.isInteger(months) || months < 1) {return "";}
    const year = Number(match[1]);
    const monthIndex = Number(match[2]) - 1;
    const day = Number(match[3]);
    const destinationMonth = monthIndex + months;
    const lastDay = new Date(Date.UTC(
        year,
        destinationMonth + 1,
        0,
    )).getUTCDate();
    const result = new Date(Date.UTC(
        year,
        destinationMonth,
        Math.min(day, lastDay),
    ));
    return result.toISOString().slice(0, 10);
}

function showMutationFailure(error, label = "Compliance changes") {
    const reason = error?.details?.reason || error?.data?.reason || "";
    const stale = [
        "stale_expected_state",
        "stale_item",
        "stale_requirement",
        "stale_record",
        "current_record_changed",
    ].includes(reason);
    openConfirmationModal(
        stale ? "Compliance changed" : `Could not save ${label}`,
        escapeHTML(stale ?
            "This Compliance data changed while the form was open. Close and reopen it before trying again." :
            error?.message || "The change could not be saved. Try again."),
        () => {},
        false,
        "OK",
        null,
        "",
        "",
        {hideCancel: true},
    );
}

function complianceCloseMessage() {
    if (modalPending) {
        return "A Compliance change is still being saved. Leave this form?";
    }
    return "Your Compliance changes have not been saved.";
}

/**
 * Routes normal Compliance modal closes through the shared dirty/pending guard.
 * Security/auth teardown uses the explicit immediate-close option.
 * @param {object} [options] Close behavior.
 * @param {boolean} [options.immediate] Bypass prompts for security teardown.
 * @param {Function} [options.onClose] Continuation after close.
 * @param {Function} [options.onStay] Continuation when editing continues.
 * @returns {boolean} Whether a Compliance modal owned the close request.
 */
export function requestComplianceModalClose(options = {}) {
    if (!modalOpen) {return false;}
    const finish = () => {
        modalDirty = false;
        modalPending = false;
        closeForm();
        options.onClose?.();
    };
    if (options.immediate || (!modalDirty && !modalPending)) {
        finish();
        return true;
    }
    openConfirmationModal(
        "Discard unsaved changes?",
        complianceCloseMessage(),
        finish,
        false,
        "Discard",
        options.onStay || null,
        "",
        "Keep editing",
        {returnFocusTarget: () => dirtyFocusTarget},
    );
    return true;
}

function beginModalSession() {
    modalSession += 1;
    modalOpen = true;
    modalDirty = false;
    modalPending = false;
    dirtyFocusTarget = null;
    const session = modalSession;
    setModalCloseGuard(() => requestComplianceModalClose());
    setModalCloseCallback(() => {
        if (session !== modalSession) {return;}
        modalSession += 1;
        modalOpen = false;
        modalDirty = false;
        modalPending = false;
        dirtyFocusTarget = null;
    });
    return session;
}

function markDirty(event) {
    modalDirty = true;
    if (event?.target instanceof HTMLElement) {
        dirtyFocusTarget = event.target;
    }
}

function setModalBusy(itemForm, busy, savingLabel = "Saving...") {
    modalPending = busy;
    const submit = itemForm.querySelector("button[type=submit]");
    if (!submit) {return;}
    if (!submit.dataset.idleLabel) {
        submit.dataset.idleLabel = submit.textContent || "Save";
    }
    submit.disabled = busy;
    submit.textContent = busy ?
        savingLabel :
        submit.dataset.idleLabel;
    itemForm.toggleAttribute("aria-busy", busy);
}

/**
 * Opens a manual tracked-item create/edit modal.
 * @param {object} options Workflow options.
 * @param {Array<object>} options.categories Authorized active categories.
 * @param {object|null} [options.item] Existing manual item.
 * @param {Function} options.onSaved Authoritative reconciliation callback.
 * @returns {void}
 */
export function openComplianceItemModal({
    categories,
    item = null,
    onSaved,
}) {
    const editing = Boolean(item?.id);
    openStandardForm("compliance_tracked_item", item, {
        getFieldsHtml: () => `
            <div class="space-y-4" data-compliance-item-form>
                <div class="${FIELD_WRAPPER_CLASSES}">
                    <label class="${FIELD_LABEL_CLASSES}" for="compliance-item-name">
                        Tracked item name ${REQUIRED_MARK_HTML}
                    </label>
                    <input id="compliance-item-name" name="name" maxlength="180"
                        required value="${escapeHTML(item?.name || "")}"
                        class="${NATIVE_INPUT_CLASSES}">
                </div>
                <div class="${FIELD_WRAPPER_CLASSES}">
                    <label class="${FIELD_LABEL_CLASSES}">
                        Category ${REQUIRED_MARK_HTML}
                    </label>
                    <div data-compliance-item-category-control></div>
                </div>
            </div>
        `,
        afterRender({itemForm}) {
            const session = beginModalSession();
            document.getElementById("form-title").textContent =
                editing ? "Edit tracked item" : "Add tracked item";
            const submit = itemForm.querySelector("button[type=submit]");
            if (submit) {
                submit.textContent = editing ? "Save changes" : "Add item";
            }
            bindNativeFieldBehavior(itemForm);
            let categoryId = item?.categoryId || "";
            const categoryRoot = itemForm.querySelector(
                "[data-compliance-item-category-control]",
            );
            createSelectControl({
                root: categoryRoot,
                name: "categoryId",
                value: categoryId,
                options: categories.map((category) => ({
                    value: category.id,
                    label: category.name,
                    order: category.order,
                })),
                searchable: categories.length >= 7,
                portalMenu: true,
                required: true,
                placeholder: "Select category",
                accessibleName: "Compliance category",
                onChange(value) {
                    categoryId = value;
                    markDirty({target: categoryRoot});
                },
            });
            itemForm.querySelectorAll("input").forEach((control) => {
                control.addEventListener("input", markDirty);
                control.addEventListener("change", markDirty);
            });
            itemForm.onsubmit = async (event) => {
                event.preventDefault();
                const nativeValid = validateNativeFormFields(itemForm);
                const controlValidation =
                    validateRegisteredControls(itemForm);
                if (!nativeValid || !controlValidation.valid) {
                    if (nativeValid) {
                        focusFirstRegisteredControlError(controlValidation);
                    }
                    return;
                }
                setModalBusy(itemForm, true, editing ? "Saving..." : "Adding...");
                try {
                    const payload = {
                        name: itemForm.elements.name.value.trim(),
                        categoryId,
                    };
                    const response = editing ?
                        await saveComplianceTrackedItem({
                            ...payload,
                            itemId: item.id,
                            expectedUpdatedAt: item.updatedAt,
                        }) :
                        await createComplianceTrackedItem(payload);
                    if (session !== modalSession) {return;}
                    modalDirty = false;
                    onSaved?.(response);
                    closeForm();
                } catch (error) {
                    if (session === modalSession) {
                        setModalBusy(itemForm, false);
                        setTimeout(() => showMutationFailure(
                            error,
                            "tracked item",
                        ), 0);
                    }
                }
            };
        },
    });
}

/**
 * Opens the one-field manual requirement-shell modal.
 * @param {object} options Workflow options.
 * @param {object} options.item Parent tracked item.
 * @param {Function} options.onSaved Authoritative reconciliation callback.
 * @returns {void}
 */
export function openComplianceRequirementModal({item, onSaved}) {
    openStandardForm("compliance_requirement", null, {
        getFieldsHtml: () => `
            <div class="${FIELD_WRAPPER_CLASSES}" data-compliance-requirement-form>
                <label class="${FIELD_LABEL_CLASSES}" for="compliance-requirement-name">
                    Requirement name ${REQUIRED_MARK_HTML}
                </label>
                <input id="compliance-requirement-name" name="name" maxlength="180"
                    required class="${NATIVE_INPUT_CLASSES}">
            </div>
        `,
        afterRender({itemForm}) {
            const session = beginModalSession();
            document.getElementById("form-title").textContent =
                "Add compliance requirement";
            const submit = itemForm.querySelector("button[type=submit]");
            if (submit) {submit.textContent = "Add requirement";}
            bindNativeFieldBehavior(itemForm);
            itemForm.elements.name.addEventListener("input", markDirty);
            itemForm.onsubmit = async (event) => {
                event.preventDefault();
                if (!validateNativeFormFields(itemForm)) {return;}
                setModalBusy(itemForm, true, "Adding...");
                try {
                    const response = await createComplianceRequirement({
                        itemId: item.id,
                        expectedItemUpdatedAt: item.updatedAt,
                        name: itemForm.elements.name.value.trim(),
                    });
                    if (session !== modalSession) {return;}
                    modalDirty = false;
                    onSaved?.(response);
                    closeForm();
                } catch (error) {
                    if (session === modalSession) {
                        setModalBusy(itemForm, false);
                        setTimeout(() => showMutationFailure(
                            error,
                            "requirement",
                        ), 0);
                    }
                }
            };
        },
    });
}

function getRecordDefaults(requirement, record, currentRecord) {
    if (record) {
        return {
            startDate: record.startDate || "",
            frequencyMonths: record.frequencyMonths ?? "",
            nextDueDate: record.nextDueDate || "",
        };
    }
    const today = getBusinessToday();
    const carriedStart = requirement.currentNextDueDate || today;
    return {
        startDate: carriedStart > today ? today : carriedStart,
        frequencyMonths: currentRecord?.frequencyMonths ?? "",
        nextDueDate: "",
    };
}

/**
 * Opens the shared record form for create or current-record correction.
 * @param {object} options Workflow options.
 * @param {object} options.item Parent tracked item.
 * @param {object} options.requirement Parent requirement.
 * @param {object|null} [options.record] Current record when correcting.
 * @param {object|null} [options.currentRecord] Current record used for create defaults.
 * @param {Function} options.onSaved Authoritative reconciliation callback.
 * @returns {void}
 */
export function openComplianceRecordModal({
    item,
    requirement,
    record = null,
    currentRecord = null,
    onSaved,
}) {
    const correcting = Boolean(record?.id);
    const defaults = getRecordDefaults(requirement, record, currentRecord);
    openStandardForm("compliance_record", record, {
        getFieldsHtml: () => `
            <div class="space-y-4 pb-10 sm:pb-0" data-compliance-record-form>
                ${correcting ? `
                    <div class="flex items-center justify-between gap-3 rounded-lg border border-gray-200 bg-gray-50 p-3">
                        <p class="text-sm text-gray-600">
                            Add, preview, version, edit, or delete evidence for
                            this record.
                        </p>
                        <button type="button" data-compliance-manage-evidence
                            class="shrink-0 text-sm font-semibold text-[var(--dfc-brand-accent)] hover:underline">
                            Manage evidence
                        </button>
                    </div>
                ` : `
                    <p class="text-sm text-gray-500">
                        Save the Compliance record before adding evidence.
                    </p>
                `}
                <div class="${FIELD_WRAPPER_CLASSES}">
                    <div data-compliance-record-start-control></div>
                </div>
                <div class="${FIELD_WRAPPER_CLASSES}">
                    <label class="${FIELD_LABEL_CLASSES}" for="compliance-record-frequency">
                        Frequency in months
                    </label>
                    <input id="compliance-record-frequency" name="frequencyMonths"
                        type="number" min="1" max="600" step="1"
                        value="${escapeHTML(String(defaults.frequencyMonths))}"
                        class="${NATIVE_INPUT_CLASSES}">
                    <p class="text-xs text-gray-500">
                        Leave blank to enter the next due date manually.
                    </p>
                </div>
                <div class="${FIELD_WRAPPER_CLASSES}">
                    <div data-compliance-record-next-due-control></div>
                    <p class="text-xs text-gray-500" data-compliance-next-due-help></p>
                </div>
            </div>
        `,
        afterRender({itemForm}) {
            const session = beginModalSession();
            document.getElementById("form-title").textContent = correcting ?
                "Correct current Compliance record" :
                "Record compliance";
            const submit = itemForm.querySelector("button[type=submit]");
            if (submit) {
                submit.textContent = correcting ?
                    "Save correction" :
                    "Record compliance";
            }
            bindNativeFieldBehavior(itemForm);
            let startDate = defaults.startDate;
            let nextDueDate = defaults.nextDueDate;
            const frequency = itemForm.elements.frequencyMonths;
            const help = itemForm.querySelector("[data-compliance-next-due-help]");
            const startRoot = itemForm.querySelector(
                "[data-compliance-record-start-control]",
            );
            const nextDueRoot = itemForm.querySelector(
                "[data-compliance-record-next-due-control]",
            );
            const syncDue = () => {
                const months = Number(frequency.value);
                const recurring = Number.isInteger(months) && months > 0;
                if (recurring) {
                    nextDueDate = calculateNextDueDate(startDate, months);
                    nextDueControl.update({
                        disabled: true,
                        value: nextDueDate,
                    });
                    help.textContent = nextDueDate ?
                        `Calculated next due: ${nextDueDate}` :
                        "Choose a start date to calculate the next due date.";
                } else {
                    if (correcting && !modalDirty) {
                        nextDueDate = defaults.nextDueDate;
                    }
                    nextDueControl.update({
                        disabled: false,
                        value: nextDueDate,
                    });
                    help.textContent = "Optional when frequency is blank.";
                }
            };
            createDateControl({
                root: startRoot,
                name: "startDate",
                nativeInputName: "startDate",
                type: "date",
                value: startDate,
                label: "Start date",
                required: true,
                mirror: false,
                onChange(value) {
                    startDate = value;
                    markDirty({target: startRoot});
                    syncDue();
                },
            });
            const nextDueControl = createDateControl({
                root: nextDueRoot,
                name: "nextDueDate",
                nativeInputName: "nextDueDate",
                type: "date",
                value: nextDueDate,
                label: "Next due",
                mirror: false,
                onChange(value) {
                    nextDueDate = value;
                    markDirty({target: nextDueRoot});
                },
            });
            [frequency].forEach((control) => {
                control.addEventListener("input", (event) => {
                    markDirty(event);
                    syncDue();
                });
                control.addEventListener("change", markDirty);
            });
            itemForm.querySelector("[data-compliance-manage-evidence]")
                ?.addEventListener("click", () => {
                    if (modalDirty) {
                        showMutationFailure(
                            new Error(
                                "Save or discard record corrections before " +
                                "managing evidence.",
                            ),
                            "Compliance evidence",
                        );
                        return;
                    }
                    void openComplianceEvidenceModal({
                        item,
                        requirement,
                        record,
                    });
                });
            syncDue();
            itemForm.onsubmit = async (event) => {
                event.preventDefault();
                const nativeValid = validateNativeFormFields(itemForm);
                const controlValidation =
                    validateRegisteredControls(itemForm);
                if (!nativeValid || !controlValidation.valid) {
                    if (nativeValid) {
                        focusFirstRegisteredControlError(controlValidation);
                    }
                    return;
                }
                const frequencyMonths = frequency.value === "" ?
                    null :
                    Number(frequency.value);
                const payload = {
                    itemId: item.id,
                    requirementId: requirement.id,
                    startDate,
                    frequencyMonths,
                    nextDueDate: frequencyMonths === null ?
                        nextDueDate || null :
                        null,
                };
                setModalBusy(
                    itemForm,
                    true,
                    correcting ? "Saving..." : "Recording...",
                );
                try {
                    const response = correcting ?
                        await correctComplianceRecord({
                            ...payload,
                            recordId: record.id,
                            expectedRecordUpdatedAt:
                                record.updatedAt || record.createdAt,
                            expectedCurrentRecordId:
                                requirement.currentRecordId,
                        }) :
                        await createComplianceRecord({
                            ...payload,
                            expectedRequirementUpdatedAt:
                                requirement.updatedAt,
                        });
                    if (session !== modalSession) {return;}
                    modalDirty = false;
                    onSaved?.(response);
                    closeForm();
                } catch (error) {
                    if (session === modalSession) {
                        setModalBusy(itemForm, false);
                        setTimeout(() => showMutationFailure(
                            error,
                            "Compliance record",
                        ), 0);
                    }
                }
            };
        },
    });
}

/**
 * Confirms deletion of the freshly current manual Compliance record.
 * @param {object} options Workflow options.
 * @param {object} options.item Parent item.
 * @param {object} options.requirement Parent requirement.
 * @param {object} options.record Current record.
 * @param {Function} options.onDeleted Authoritative reconciliation callback.
 * @returns {void}
 */
export function confirmComplianceRecordDelete({
    item,
    requirement,
    record,
    onDeleted,
}) {
    const reasonId = `compliance-record-delete-reason-${record.id}`;
    openConfirmationModal(
        "Delete current Compliance record?",
        `
            <p>This permanently deletes the current record. The next eligible historical record will be promoted, or the requirement will return to no current record.</p>
            <label class="mt-4 block text-sm text-gray-600" for="${escapeHTML(reasonId)}">Reason</label>
            <textarea id="${escapeHTML(reasonId)}" maxlength="500"
                class="${NATIVE_INPUT_CLASSES} mt-1 h-20 resize-y"
                placeholder="Optional reason"></textarea>
        `,
        async () => {
            const reason = document.getElementById(reasonId)?.value.trim();
            try {
                const response = await deleteComplianceRecord({
                    itemId: item.id,
                    requirementId: requirement.id,
                    recordId: record.id,
                    expectedCurrentRecordId: requirement.currentRecordId,
                    expectedCurrentRecordUpdatedAt:
                        record.updatedAt || record.createdAt,
                    reason: reason || null,
                    confirmed: true,
                });
                onDeleted?.(response);
            } catch (error) {
                setTimeout(() => showMutationFailure(
                    error,
                    "Compliance record",
                ), 0);
                throw error;
            }
        },
        true,
        "Delete record",
        null,
        "Deleting...",
        "Cancel",
        {
            closeOnError: false,
            initialFocusTarget: () => document.getElementById(reasonId),
        },
    );
}

/**
 * Immediately closes the Compliance-owned standard modal during route teardown.
 * @returns {void}
 */
export function deactivateComplianceModal() {
    requestComplianceModalClose({immediate: true});
}
